import { z } from 'zod';

export const sessionStateSchema = z.enum(['RUNNING', 'PAUSED', 'STOPPED']);
export type SessionState = z.infer<typeof sessionStateSchema>;

const ticketIdsArray = z
  .array(z.string().min(1, 'ticketId is required'))
  .min(1, 'At least one ticketId is required')
  .max(10, 'At most 10 ticketIds');

/**
 * timeSessionStartSchema — TKT-05: sessions may attach multiple tickets up front.
 */
export const timeSessionStartSchema = z
  .object({
    ticketIds: ticketIdsArray,
    note: z.string().max(1000, 'note must be 1000 characters or fewer').optional(),
  })
  .strict();
export type TimeSessionStartInput = z.infer<typeof timeSessionStartSchema>;

export const timeSessionPauseSchema = z.object({}).strict();
export type TimeSessionPauseInput = z.infer<typeof timeSessionPauseSchema>;

export const timeSessionStopSchema = z.object({}).strict();
export type TimeSessionStopInput = z.infer<typeof timeSessionStopSchema>;

/**
 * timeSessionAdjustSchema — TIME-04: manual duration override requires reason
 * (non-empty, max 500 chars). adjustedDurationMinutes capped at 24h.
 */
export const timeSessionAdjustSchema = z
  .object({
    adjustedDurationMinutes: z
      .number()
      .int('adjustedDurationMinutes must be a whole number')
      .min(0, 'adjustedDurationMinutes must be zero or greater')
      .max(24 * 60, 'adjustedDurationMinutes must be 1440 or fewer (24 hours)'),
    reason: z
      .string()
      .trim()
      .min(1, 'reason is required (TIME-04)')
      .max(500, 'reason must be 500 characters or fewer'),
  })
  .strict();
export type TimeSessionAdjustInput = z.infer<typeof timeSessionAdjustSchema>;

/**
 * timeSessionWaiveSchema — TIME-05: waiving a session requires a reason
 * and an AuditLog entry (the reason is captured here at the contract layer).
 */
export const timeSessionWaiveSchema = z
  .object({
    reason: z
      .string()
      .trim()
      .min(1, 'reason is required (TIME-05)')
      .max(500, 'reason must be 500 characters or fewer'),
  })
  .strict();
export type TimeSessionWaiveInput = z.infer<typeof timeSessionWaiveSchema>;

/**
 * sessionAttachTicketsSchema — TKT-05: attach more tickets to an in-flight session.
 */
export const sessionAttachTicketsSchema = z
  .object({
    ticketIds: ticketIdsArray,
  })
  .strict();
export type SessionAttachTicketsInput = z.infer<typeof sessionAttachTicketsSchema>;

/**
 * timeSessionSummarySchema — response shape. incrementsBilled is computed
 * server-side as Math.ceil(durationMinutes / 20) (TIME-03).
 */
export const timeSessionSummarySchema = z.object({
  id: z.string(),
  companyId: z.string(),
  agentId: z.string(),
  ticketIds: z.array(z.string()),
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime().nullable(),
  durationMinutes: z.number().int().min(0),
  adjustedDurationMinutes: z.number().int().min(0).nullable(),
  adjustmentReason: z.string().nullable(),
  incrementsBilled: z.number().int().min(0),
  state: sessionStateSchema,
  isWaived: z.boolean(),
  waiveReason: z.string().nullable(),
  note: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TimeSessionSummary = z.infer<typeof timeSessionSummarySchema>;
